import { useState, useEffect, FormEvent } from 'react'
import './AllergyForm.css'
import type { Allergy, AllergyFormData, AllergyCategory, AllergyTriggerType, ReactionSeverity } from '../types/allergy'

interface AllergyFormProps {
  patientId: string
  initialAllergy?: Allergy
  onSubmit: (data: AllergyFormData & { patientId: string }) => Promise<void> | void
  onCancel: () => void
}

const CATEGORY_OPTIONS: { value: AllergyCategory; label: string }[] = [
  { value: 'true-allergy', label: 'True Allergy' },
  { value: 'intolerance', label: 'Intolerance' },
  { value: 'adverse-reaction', label: 'Adverse Reaction' },
]

const TRIGGER_TYPE_OPTIONS: { value: AllergyTriggerType; label: string }[] = [
  { value: 'drug', label: 'Drug / medication' },
  { value: 'food', label: 'Food' },
  { value: 'environmental', label: 'Environmental' },
  { value: 'other', label: 'Other' },
]

const SEVERITY_OPTIONS: { value: ReactionSeverity; label: string }[] = [
  { value: 'mild', label: 'Mild' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'severe', label: 'Severe' },
  { value: 'life-threatening', label: 'Life-threatening' },
  { value: 'unknown', label: 'Unknown / not recorded' },
]

export function AllergyForm({ patientId, initialAllergy, onSubmit, onCancel }: AllergyFormProps) {
  const [category, setCategory] = useState<AllergyCategory>('true-allergy')
  const [triggerType, setTriggerType] = useState<AllergyTriggerType>('drug')
  const [triggerDisplay, setTriggerDisplay] = useState('')
  const [triggerCode, setTriggerCode] = useState('')
  const [severity, setSeverity] = useState<ReactionSeverity>('unknown')
  const [reactionDescription, setReactionDescription] = useState('')
  const [onset, setOnset] = useState('')
  const [systemInvolved, setSystemInvolved] = useState('')
  const [recordedDate, setRecordedDate] = useState('')
  const [notes, setNotes] = useState('')
  const [status, setStatus] = useState<'active' | 'inactive'>('active')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10)
    if (initialAllergy) {
      setCategory(initialAllergy.category)
      setTriggerType(initialAllergy.triggerType)
      setTriggerDisplay(initialAllergy.triggerDisplay)
      setTriggerCode(initialAllergy.triggerCode || '')
      setSeverity(initialAllergy.reaction?.severity || 'unknown')
      setReactionDescription(initialAllergy.reaction?.description || '')
      setOnset(initialAllergy.reaction?.onset || '')
      setSystemInvolved(initialAllergy.reaction?.systemInvolved || '')
      setRecordedDate(initialAllergy.recordedDate || today)
      setNotes(initialAllergy.notes || '')
      setStatus(initialAllergy.status)
    } else {
      setCategory('true-allergy')
      setTriggerType('drug')
      setSeverity('unknown')
      setRecordedDate(today)
      setNotes('')
      setStatus('active')
    }
  }, [initialAllergy])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!triggerDisplay.trim()) {
      setError('Please enter the allergy trigger (e.g. drug, food, or agent).')
      return
    }

    const formData: AllergyFormData & { patientId: string } = {
      patientId,
      category,
      triggerType,
      triggerDisplay: triggerDisplay.trim(),
      triggerCode: triggerCode.trim() || undefined,
      reaction: {
        severity,
        description: reactionDescription.trim() || undefined,
        onset: onset.trim() || undefined,
        systemInvolved: systemInvolved.trim() || undefined,
      },
      recordedDate,
      notes: notes.trim() || undefined,
      status,
    }

    setSubmitting(true)
    try {
      await onSubmit(formData)
    } catch (err: any) {
      setError(err?.message || 'Failed to save allergy')
    } finally {
      setSubmitting(false)
    }
  }

  const isEditing = !!initialAllergy

  return (
    <form
      className="allergy-form"
      onSubmit={handleSubmit}
    >
      <div className="allergy-form-header">
        <h2>{isEditing ? 'Edit Allergy / Reaction' : 'Add Allergy / Reaction'}</h2>
        <p className="form-subtitle">
          Capture true allergies, intolerances and adverse reactions with structured detail.
        </p>
      </div>

      {error && <div className="form-error">{error}</div>}

      <div className="form-grid">
        <div className="form-field">
          <label>
            Category<span className="required">*</span>
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as AllergyCategory)}
          >
            {CATEGORY_OPTIONS.map((opt) => (
              <option
                key={opt.value}
                value={opt.value}
              >
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label>
            Trigger type<span className="required">*</span>
          </label>
          <select
            value={triggerType}
            onChange={(e) => setTriggerType(e.target.value as AllergyTriggerType)}
          >
            {TRIGGER_TYPE_OPTIONS.map((opt) => (
              <option
                key={opt.value}
                value={opt.value}
              >
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field full-width">
          <label>
            Trigger (drug, food, agent)<span className="required">*</span>
          </label>
          <input
            type="text"
            value={triggerDisplay}
            onChange={(e) => setTriggerDisplay(e.target.value)}
            placeholder="e.g. Amoxicillin, Peanuts, Wasp sting"
          />
          <p className="field-help">
            Use a clear, recognisable name. Drug lookups and coding can be added later.
          </p>
        </div>

        <div className="form-field">
          <label>Trigger code (optional)</label>
          <input
            type="text"
            value={triggerCode}
            onChange={(e) => setTriggerCode(e.target.value)}
            placeholder="e.g. HPRA / ATC code"
          />
        </div>

        <div className="form-field">
          <label>Severity</label>
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value as ReactionSeverity)}
          >
            {SEVERITY_OPTIONS.map((opt) => (
              <option
                key={opt.value}
                value={opt.value}
              >
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field full-width">
          <label>Reaction description</label>
          <textarea
            value={reactionDescription}
            onChange={(e) => setReactionDescription(e.target.value)}
            placeholder="e.g. Generalised urticaria, lip swelling, wheeze within 30 minutes of exposure"
            rows={3}
          />
        </div>

        <div className="form-field">
          <label>Onset</label>
          <input
            type="text"
            value={onset}
            onChange={(e) => setOnset(e.target.value)}
            placeholder="e.g. Immediate, Delayed, Unknown"
          />
        </div>

        <div className="form-field">
          <label>System involved</label>
          <input
            type="text"
            value={systemInvolved}
            onChange={(e) => setSystemInvolved(e.target.value)}
            placeholder="e.g. Respiratory, Cutaneous, Cardiovascular"
          />
        </div>

        <div className="form-field">
          <label>
            Date recorded<span className="required">*</span>
          </label>
          <input
            type="date"
            value={recordedDate}
            onChange={(e) => setRecordedDate(e.target.value)}
          />
        </div>

        <div className="form-field">
          <label>Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as 'active' | 'inactive')}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div className="form-field full-width">
          <label>Clinical notes / rationale</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Additional clinical context, e.g. how the reaction was confirmed or why an entry was deactivated."
            rows={3}
          />
        </div>
      </div>

      <div className="form-actions">
        <button
          type="button"
          className="btn-secondary"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={submitting}
        >
          {submitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Allergy'}
        </button>
      </div>
    </form>
  )
}

